import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import Markdown from '@/components/markdown/Markdown';

interface VoiceQuoteLookup {
  conversation_id: string;
  message_id?: string;
}

/**
 * Extract citation data from the content inside a <quote> tag. We
 * accept several historical formats the summary LLMs have emitted:
 *
 *   {conversationId: abc, respondent_index: 3}     — braces + keys (canonical)
 *   {questionId: q, conversationId: abc, respondent_index: 3}
 *   conversationId: abc, respondent_index: 3       — no braces, with keys
 *   abc, respondent_index: 3                       — no braces, bare id + index
 *   "Dancing brings people together"               — actual quote text (not a citation)
 *
 * The last case (actual quote text) is rejected by the `respondent_index`
 * requirement — if the tag contains no index marker it isn't a citation
 * and we skip it entirely so the prose renders without a stray button.
 */
function parseCitation(
  raw: string,
): {
  conversationId: string;
  respondentIndex: number;
  questionId?: string;
} | null {
  const stripped = raw.replace(/^\s*\{?\s*|\s*\}?\s*$/g, '');

  const indexMatch = stripped.match(/respondent_index:\s*(\d+)/);
  if (!indexMatch) return null;

  const keyMatch = stripped.match(/conversationId:\s*([a-f0-9]{24})/i);
  const bareMatch = stripped.match(/^([a-f0-9]{24})/);
  const conversationId = keyMatch?.[1] || bareMatch?.[1];
  if (!conversationId) return null;

  // Extract optional questionId — used as fallback scroll target by the
  // transcript panel when the cited respondent isn't in the curated
  // voice_quotes set (so we have no exact message_id to land on).
  // Question IDs in this codebase are UUIDv4 strings, not Mongo ObjectIds.
  const questionMatch = stripped.match(
    /questionId:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i,
  );

  return {
    conversationId,
    respondentIndex: parseInt(indexMatch[1], 10),
    questionId: questionMatch?.[1],
  };
}

const QUOTE_REGEX = /<quote>\s*([\s\S]*?)\s*<\/quote>/g;

/**
 * Render text that contains `<quote>` citation tags alongside
 * markdown. Paragraph breaks (`\n\n`) are preserved as real `<p>`
 * blocks, citations render as inline respondent-number buttons that
 * open the transcript panel.
 *
 * Previously this component wrapped the whole output in a single
 * `<span>` whenever any citation was present, which collapsed
 * paragraph spacing and bullet formatting. The new approach splits
 * the text into paragraphs first, THEN parses citations within each
 * paragraph — so each paragraph is a proper block and citations sit
 * inline inside the paragraph text where the AI placed them.
 */
function TextWithClickableQuotes({
  text,
  voiceQuotes,
  fallbackQuestionId,
}: {
  text: string;
  voiceQuotes?: VoiceQuoteLookup[];
  /**
   * Question context for citation clicks that have no exact message_id
   * lookup (i.e., the cited respondent isn't in the curated
   * voice_quotes set). When the click handler can't find an exact
   * message, it forwards this question_id to the transcript panel so
   * the panel can scroll to that respondent's answer for the question.
   *
   * On per-question pages, pass the page's question_id. On the overall
   * page, individual citations may carry their own questionId in the
   * tag (extracted from `parseCitation`) — that takes precedence over
   * this prop.
   */
  fallbackQuestionId?: string;
}) {
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  const messageIdMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!voiceQuotes) return map;
    for (const vq of voiceQuotes) {
      if (vq.conversation_id && vq.message_id && !map.has(vq.conversation_id)) {
        map.set(vq.conversation_id, vq.message_id);
      }
    }
    return map;
  }, [voiceQuotes]);

  const hasQuotes = /<quote>/.test(text);

  // Build a citation button (inline-flex so it sits within a paragraph).
  const citationButton = (citation: {
    conversationId: string;
    respondentIndex: number;
    questionId?: string;
  }) => (
    <button
      key={uuidv4()}
      type="button"
      onClick={() => {
        const messageId = messageIdMap.get(citation.conversationId);
        // Per-citation questionId (from the tag) wins over the
        // page-level fallback. Either is used by the transcript panel
        // to derive a scroll target when no exact messageId is known.
        const fallback = citation.questionId || fallbackQuestionId;
        openTranscriptPanel(citation.conversationId, messageId, fallback);
      }}
      className="inline-flex items-center justify-center min-w-5 h-5 px-1 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-primary opacity-90 hover:opacity-100 transition-opacity align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1"
    >
      {citation.respondentIndex}
    </button>
  );

  // Process ONE paragraph: split by <quote> tags, interleave inline
  // markdown chunks with citation buttons.
  const renderParagraph = (para: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // Fresh regex per call because /g state is shared.
    const regex = new RegExp(QUOTE_REGEX.source, 'g');

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(para)) !== null) {
      const citation = parseCitation(match[1]);
      if (!citation) {
        // Tag isn't a citation (e.g., LLM wrapped raw quote text) —
        // drop the tag entirely so the surrounding prose flows cleanly.
        lastIndex = regex.lastIndex;
        continue;
      }
      const before = para.slice(lastIndex, match.index);
      if (before) {
        // Inline markdown inside the paragraph: turn <p> into <span>
        // so we don't get nested <p> elements.
        result.push(
          <Markdown key={uuidv4()} isParagraphSpanEl>
            {before}
          </Markdown>,
        );
      }
      result.push(citationButton(citation));
      lastIndex = regex.lastIndex;
    }

    const after = para.slice(lastIndex);
    if (after) {
      result.push(
        <Markdown key={uuidv4()} isParagraphSpanEl>
          {after}
        </Markdown>,
      );
    }
    return result;
  };

  // Fast-path: no citations → delegate everything to block Markdown
  // (handles paragraphs, lists, bold, headings correctly out of the
  // box). Keeps the pre-refactor behaviour for the common case.
  if (!hasQuotes) {
    return <Markdown>{text}</Markdown>;
  }

  // With citations: split into paragraphs so each becomes its own
  // block with proper spacing. A paragraph here is any run of text
  // separated by one or more blank lines.
  const paragraphs = text.split(/\n{2,}/);

  return (
    <>
      {paragraphs.map((para) => {
        const trimmed = para.trim();
        if (!trimmed) return null;
        return (
          <p
            key={uuidv4()}
            className="mb-5 last:mb-0 leading-relaxed"
          >
            {renderParagraph(trimmed)}
          </p>
        );
      })}
    </>
  );
}

export default TextWithClickableQuotes;
